import React, { useState, useEffect } from 'react';
import {
  FaChartBar,
  FaCarSide,
  FaMoneyBillWave,
  FaUserTie,
  FaCheckCircle,
  FaShieldAlt,
  FaPrint,
  FaDownload,
  FaSearch,
  FaFilter,
  FaExternalLinkAlt,
  FaEye,
  FaFileContract,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getBackendBaseUrl } from '../../../utils/apiUrl';
import DealPrintReport from '../Customer/DealPrintReport';

const D2Reports = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSalesperson, setSelectedSalesperson] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedDealForPrint, setSelectedDealForPrint] = useState(null);

  const fetchReportsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setDeals(data.data);
      }
    } catch (err) {
      console.error("Failed to load deals for reporting", err);
      toast.error("Failed to load reports data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportsData();
  }, []);

  // Compute Aggregates & KPIs
  const totalDeals = deals.length;
  const completedDeals = deals.filter(d => d.status === "APPROVED" || d.status === "COMPLETED" || d.status === "CONTRACT_GENERATED").length;
  const inProgressDeals = deals.filter(d => d.status === "IN_PROGRESS" || d.status === "SUBMITTED").length;

  const totalGrossVolume = deals.reduce((sum, d) => {
    const price = Number(d.vehicle?.salePrice || d.vehicle?.msrp || d.calculation?.netAmount || 0);
    return sum + price;
  }, 0);

  const totalFinancedVolume = deals.reduce((sum, d) => {
    const fin = Number(d.calculation?.totalFinanced || d.finance?.amountFinanced || 0);
    return sum + fin;
  }, 0);

  const totalFAndIProducts = deals.reduce((sum, d) => {
    const count = Array.isArray(d.fAndI?.products) ? d.fAndI.products.length : 0;
    return sum + count;
  }, 0);

  const averageDealSize = completedDeals > 0 ? Math.round(totalGrossVolume / (completedDeals || 1)) : 0;

  // Group deals by Sales Consultant
  const salespersonMap = {};
  deals.forEach((d) => {
    let spName = "Unassigned Desk";
    let spId = "N/A";

    if (d.salespersonId && typeof d.salespersonId === "object") {
      spName = `${d.salespersonId.firstName || ''} ${d.salespersonId.lastName || ''}`.trim() || d.salespersonId.name || "Sales Consultant";
      spId = d.salespersonId._id || d.salespersonId.id;
    } else if (d.salespersonId && typeof d.salespersonId === "string") {
      spName = d.salespersonId;
      spId = d.salespersonId;
    }

    if (!salespersonMap[spName]) {
      salespersonMap[spName] = {
        name: spName,
        id: spId,
        totalDeals: 0,
        completedDeals: 0,
        totalVolume: 0,
        fAndICount: 0,
        dealsList: []
      };
    }

    salespersonMap[spName].totalDeals += 1;
    if (d.status === "APPROVED" || d.status === "COMPLETED" || d.status === "CONTRACT_GENERATED") {
      salespersonMap[spName].completedDeals += 1;
    }
    const saleAmt = Number(d.vehicle?.salePrice || d.vehicle?.msrp || 0);
    salespersonMap[spName].totalVolume += saleAmt;
    salespersonMap[spName].fAndICount += Array.isArray(d.fAndI?.products) ? d.fAndI.products.length : 0;
    salespersonMap[spName].dealsList.push(d);
  });

  const salespersonRankings = Object.values(salespersonMap).sort((a, b) => b.totalVolume - a.totalVolume);

  // Filter deals list
  const filteredDeals = deals.filter((d) => {
    let spName = "Unassigned Desk";
    if (d.salespersonId && typeof d.salespersonId === "object") {
      spName = `${d.salespersonId.firstName || ''} ${d.salespersonId.lastName || ''}`.trim() || d.salespersonId.name || "Sales Consultant";
    } else if (d.salespersonId && typeof d.salespersonId === "string") {
      spName = d.salespersonId;
    }

    const matchesSalesperson = selectedSalesperson === "ALL" || spName === selectedSalesperson;
    const matchesStatus = selectedStatus === "ALL" || d.status === selectedStatus;

    const customer = (d.customer?.name || "").toLowerCase();
    const dealNum = (d.dealNumber || "").toLowerCase();
    const veh = `${d.vehicle?.year || ''} ${d.vehicle?.make || ''} ${d.vehicle?.model || ''}`.toLowerCase();
    const matchesSearch = !searchQuery || customer.includes(searchQuery.toLowerCase()) || dealNum.includes(searchQuery.toLowerCase()) || veh.includes(searchQuery.toLowerCase());

    return matchesSalesperson && matchesStatus && matchesSearch;
  });

  const exportCSV = () => {
    if (deals.length === 0) {
      toast.info("No deals data to export");
      return;
    }

    const headers = ["Deal Number", "Customer Name", "Salesperson", "Vehicle", "Sale Price", "Amount Financed", "Status", "Created Date"];
    const rows = deals.map(d => [
      d.dealNumber || "N/A",
      `"${d.customer?.name || 'Unnamed'}"`,
      `"${typeof d.salespersonId === 'object' ? (d.salespersonId?.name || d.salespersonId?.firstName || 'Desk') : (d.salespersonId || 'Desk')}"`,
      `"${d.vehicle?.year || ''} ${d.vehicle?.make || ''} ${d.vehicle?.model || ''}"`,
      d.vehicle?.salePrice || 0,
      d.calculation?.totalFinanced || 0,
      d.status || "DRAFT",
      new Date(d.createdAt || Date.now()).toLocaleDateString()
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `dealership_sales_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Sales report CSV exported successfully!");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "APPROVED":
      case "COMPLETED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "SUBMITTED":
        return "bg-cyan-50 text-cyan-700 border-cyan-200";
      case "IN_PROGRESS":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-slate-50 text-slate-600 border-slate-200";
    }
  };

  return (
    <div className="p-6 sm:p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up font-sans">

      {/* Title & Action Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl shadow-sm border border-indigo-100">
              <FaChartBar size={24} />
            </span>
            Dealership Performance & Sales Reports
          </h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1 leading-relaxed">
            Monitor real-time sales volume, closed deals, F&I product attachment, and consultant rankings.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={exportCSV}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold border border-slate-200 shadow-sm transition-all cursor-pointer"
          >
            <FaDownload className="text-indigo-600" /> Export CSV
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-md shadow-indigo-200 transition-all cursor-pointer"
          >
            <FaPrint /> Print Summary
          </button>
        </div>
      </div>

      {/* Real-time Executive KPI Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {/* Metric 1 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Sales Volume</span>
            <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600">
              <FaMoneyBillWave size={16} />
            </div>
          </div>
          <p className="text-2xl font-black text-slate-800">${totalGrossVolume.toLocaleString()}</p>
          <div className="flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-emerald-600">
            <span>✓ {totalDeals} Deals Created Across Showrooms</span>
          </div>
        </div>

        {/* Metric 2 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Deals Approved / Closed</span>
            <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
              <FaCheckCircle size={16} />
            </div>
          </div>
          <p className="text-2xl font-black text-emerald-600">{completedDeals}</p>
          <div className="flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-slate-500">
            <span>{inProgressDeals} Deals currently in progress</span>
          </div>
        </div>

        {/* Metric 3 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Financed Volume</span>
            <div className="p-2.5 rounded-xl bg-cyan-50 text-cyan-600">
              <FaCarSide size={16} />
            </div>
          </div>
          <p className="text-2xl font-black text-slate-800">${totalFinancedVolume.toLocaleString()}</p>
          <div className="flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-cyan-600">
            <span>Lender Portfolio Underwriting</span>
          </div>
        </div>

        {/* Metric 4 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">F&I Protection Products</span>
            <div className="p-2.5 rounded-xl bg-amber-50 text-amber-600">
              <FaShieldAlt size={16} />
            </div>
          </div>
          <p className="text-2xl font-black text-amber-600">{totalFAndIProducts}</p>
          <div className="flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-slate-500">
            <span>Warranty & GAP Contracts Attached</span>
          </div>
        </div>
      </div>

      {/* SECTION 1: SALES CONSULTANT PERFORMANCE BREAKDOWN TABLE */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6 mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-100 pb-4 mb-4">
          <div>
            <h3 className="text-base font-extrabold text-slate-800 flex items-center gap-2">
              <FaUserTie className="text-indigo-600" /> Sales Consultant Performance & Commission Attribution
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Live breakdown of deals originated and closed per salesperson.
            </p>
          </div>
          <span className="text-xs font-bold bg-indigo-50 text-indigo-600 px-3 py-1 rounded-lg">
            {salespersonRankings.length} Active Consultants
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200/60">
                <th className="px-4 py-3">Sales Consultant</th>
                <th className="px-4 py-3 text-center">Total Originated</th>
                <th className="px-4 py-3 text-center">Approved / Closed</th>
                <th className="px-4 py-3 text-right">Gross Sales Volume</th>
                <th className="px-4 py-3 text-center">F&I Products</th>
                <th className="px-4 py-3 text-right">Close Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
              {salespersonRankings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-slate-400 italic">
                    No salesperson activity logged yet.
                  </td>
                </tr>
              ) : (
                salespersonRankings.map((sp, idx) => {
                  const rate = sp.totalDeals > 0 ? Math.round((sp.completedDeals / sp.totalDeals) * 100) : 0;
                  return (
                    <tr key={sp.name + idx} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3.5 font-bold text-slate-900 flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-indigo-500 to-blue-500 text-white flex items-center justify-center text-[10px] font-black shadow-sm">
                          {sp.name.charAt(0)}
                        </div>
                        <div>
                          <span>{sp.name}</span>
                          <span className="block text-[10px] text-slate-400 font-normal">ID: {sp.id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-center font-bold text-slate-800">{sp.totalDeals}</td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-md border border-emerald-200/60">
                          {sp.completedDeals}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right font-bold text-slate-900">${sp.totalVolume.toLocaleString()}</td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="bg-amber-50 text-amber-700 font-bold px-2 py-0.5 rounded-md border border-amber-200/60">
                          {sp.fAndICount}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="font-extrabold text-indigo-600">{rate}%</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECTION 2: LIVE DEALS LOG & AUDIT REPORT */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-6 flex-1 flex flex-col">

        {/* Table Filters Bar */}
        <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 border-b border-slate-100 pb-4 mb-4">
          <div>
            <h3 className="text-base font-extrabold text-slate-800 flex items-center gap-2">
              <FaFileContract className="text-indigo-600" /> Complete Deals Log & Customer Documentation
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Access individual deal jackets, verified customer documents, and export official Buyer's Orders.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search Input */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={11} />
              <input
                type="text"
                placeholder="Search deal #, customer, car..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-600 focus:bg-white transition-all w-48 sm:w-56"
              />
            </div>

            {/* Salesperson Filter */}
            <select
              value={selectedSalesperson}
              onChange={(e) => setSelectedSalesperson(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-700 font-medium outline-none focus:border-indigo-600"
            >
              <option value="ALL">All Salespeople</option>
              {salespersonRankings.map(sp => (
                <option key={sp.name} value={sp.name}>{sp.name}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs text-slate-700 font-medium outline-none focus:border-indigo-600"
            >
              <option value="ALL">All Statuses</option>
              <option value="APPROVED">Approved / Closed</option>
              <option value="COMPLETED">Completed</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="DRAFT">Draft</option>
            </select>
          </div>
        </div>

        {/* Deals Table */}
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200/60">
                <th className="px-4 py-3">Deal #</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Vehicle</th>
                <th className="px-4 py-3">Sales Consultant</th>
                <th className="px-4 py-3 text-right">Sale Price</th>
                <th className="px-4 py-3 text-center">Docs Status</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-slate-400">
                    <span className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin inline-block mr-2" />
                    Loading dealership reports...
                  </td>
                </tr>
              ) : filteredDeals.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-slate-400 italic">
                    No deals match the specified filters.
                  </td>
                </tr>
              ) : (
                filteredDeals.map((deal) => {
                  const spDisplay = deal.salespersonId && typeof deal.salespersonId === "object"
                    ? `${deal.salespersonId.firstName || ''} ${deal.salespersonId.lastName || ''}`.trim() || deal.salespersonId.name || "Sales Consultant"
                    : (deal.salespersonId || "Unassigned");

                  const docCount = Object.keys(deal.uploadedDocuments || {}).length;

                  return (
                    <tr key={deal._id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-4 py-3 font-mono font-bold text-slate-900">{deal.dealNumber || "PHS-00000"}</td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900">{deal.customer?.name || "Valued Customer"}</div>
                        <div className="text-[10px] text-slate-400">{deal.customer?.phone || deal.customer?.email || "-"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900">
                          {deal.vehicle?.year} {deal.vehicle?.make} {deal.vehicle?.model}
                        </div>
                        <div className="text-[10px] text-slate-400">{deal.vehicle?.trim || "Standard"} &middot; Stock: {deal.vehicle?.stockNumber || "-"}</div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-800">{spDisplay}</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900">
                        ${(deal.vehicle?.salePrice || deal.vehicle?.msrp || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {docCount > 0 ? (
                          <span className="bg-emerald-50 text-emerald-700 border border-emerald-200/60 text-[10px] font-bold px-2 py-0.5 rounded-full">
                            ✓ {docCount} Uploaded
                          </span>
                        ) : (
                          <span className="bg-slate-100 text-slate-500 text-[10px] font-medium px-2 py-0.5 rounded-full">
                            Pending Docs
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase border ${getStatusBadge(deal.status)}`}>
                          {deal.status?.replace(/_/g, " ") || "DRAFT"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setSelectedDealForPrint(deal)}
                          className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-[11px] shadow-sm transition-all flex items-center gap-1.5 ml-auto cursor-pointer"
                          title="View"
                        >
                          <FaPrint size={10} className="text-cyan-300" /> Print Deal Sheet
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Official Printable Buyer's Order Modal */}
      {selectedDealForPrint && (
        <DealPrintReport
          deal={selectedDealForPrint}
          dealershipData={{
            name: "Pohanka Automotive Group",
            address: "123 Main Street, Salisbury, MD",
            phone: "(555) 234-5678",
            tax_rate: 0.07,
            doc_fee: 800,
            registration_fee: 772
          }}
          activeCar={selectedDealForPrint.vehicle || {}}
          customerName={selectedDealForPrint.customer?.name || "Valued Customer"}
          salespersonId={
            typeof selectedDealForPrint.salespersonId === "object"
              ? (selectedDealForPrint.salespersonId?.name || selectedDealForPrint.salespersonId?.firstName || "Sales Consultant")
              : (selectedDealForPrint.salespersonId || "Sales Consultant")
          }
          salePrice={selectedDealForPrint.vehicle?.salePrice || 34200}
          docFee={800}
          registrationFee={772}
          taxRate={0.07}
          salesTax={(selectedDealForPrint.vehicle?.salePrice || 34200) * 0.07}
          accessories={selectedDealForPrint.vehicle?.accessories || 0}
          totalAmountDue={selectedDealForPrint.calculation?.totalDue || 36400}
          downPayment={selectedDealForPrint.finance?.downPayment || 2500}
          customerRebate={selectedDealForPrint.finance?.rebate || 0}
          tradeEquity={selectedDealForPrint.trade?.equity || 0}
          hasTrade={!!selectedDealForPrint.trade?.value}
          baseAmountFinanced={selectedDealForPrint.calculation?.baseFinanced || 33900}
          totalFinanced={selectedDealForPrint.calculation?.totalFinanced || 33900}
          totalFAndIAmount={0}
          paymentPreference={selectedDealForPrint.finance?.paymentPreference || "FINANCE"}
          apr={selectedDealForPrint.finance?.apr || 5.99}
          termMonths={selectedDealForPrint.finance?.term || 72}
          monthlyPayment={selectedDealForPrint.calculation?.payment || 540}
          selectedProductsList={selectedDealForPrint.fAndI?.products || []}
          uploadedDocs={selectedDealForPrint.uploadedDocuments || {}}
          customerSignature={selectedDealForPrint.signature}
          onClose={() => setSelectedDealForPrint(null)}
        />
      )}

    </div>
  );
};

export default D2Reports;
